import React from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import { Container, Row, Col, Card, CardHeader, Button, Modal, ModalBody, ModalFooter } from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useAuthContext } from "../../Components/AuthContext";
import { SUBDOMAIN } from "../../assets/meta/meta";
import { membershipService } from "../../api/services/membership.service";
import { SiteMember } from "../../api/types/member";
import MemberRegisterModal from "./MemberRegisterModal";
import {
  ensureCurrentPermissionOption,
  getPermissionOptions,
  permissionLabelMap,
} from "./memberPermissions";

const canManageMemberMenu = (userInfo: any, requiredPermission: number = 1) => {
  if (!userInfo?.site_permission) return false;
  const permission = Number(userInfo.site_permission.permission);
  if (!Number.isFinite(permission) || permission < requiredPermission) return false;
  if (permission === 2) return true;
  return userInfo.site_permission.subdomain === SUBDOMAIN;
};

const MemberListPage = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuthContext();
  const sitePermission = userInfo?.site_permission;
  const actorPermission = Number(sitePermission?.permission || 0);
  const canManage = canManageMemberMenu(userInfo, 1);

  const [members, setMembers] = React.useState<SiteMember[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = React.useState(false);
  const [passwordMember, setPasswordMember] = React.useState<SiteMember | null>(null);
  const [passwordValue, setPasswordValue] = React.useState("");
  const [passwordConfirmValue, setPasswordConfirmValue] = React.useState("");
  const [isPasswordSubmitting, setIsPasswordSubmitting] = React.useState(false);

  const permissionOptions = React.useMemo(
    () => getPermissionOptions(actorPermission),
    [actorPermission]
  );

  const fetchMembers = React.useCallback(async () => {
    if (!sitePermission?.site) return;
    setIsLoading(true);
    try {
      const response = await membershipService.getSiteMembers(sitePermission.site);
      setMembers(response.data || []);
    } catch (error) {
      console.log(error);
      toast.error("멤버 목록을 불러오지 못했습니다.");
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, [sitePermission?.site]);

  React.useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const onPermissionUpdate = async (member: SiteMember, permission: number) => {
    if (!sitePermission?.site) return;
    if (member.user_id === userInfo?.id) {
      toast.error("본인 권한은 직접 변경할 수 없습니다.");
      return;
    }

    try {
      await membershipService.updateMemberPermission(sitePermission.site, member.user_id, {
        permission,
      });
      toast.success("권한을 변경했습니다.");
      await fetchMembers();
    } catch (error) {
      console.log(error);
      toast.error("권한 변경에 실패했습니다.");
    }
  };

  const onDeleteMember = async (member: SiteMember) => {
    if (!sitePermission?.site) return;
    if (member.user_id === userInfo?.id) {
      toast.error("본인 멤버십은 삭제할 수 없습니다.");
      return;
    }

    const confirmed = await dialogService.confirm(
      `${member.username || member.phone_num} 멤버를 지점에서 삭제하시겠습니까?`
    );
    if (!confirmed) return;

    try {
      await membershipService.deleteSiteMember(sitePermission.site, member.user_id);
      toast.success("멤버를 삭제했습니다.");
      await fetchMembers();
    } catch (error) {
      console.log(error);
      toast.error("멤버 삭제에 실패했습니다.");
    }
  };

  const openPasswordModal = (member: SiteMember) => {
    setPasswordMember(member);
    setPasswordValue("");
    setPasswordConfirmValue("");
  };

  const closePasswordModal = () => {
    if (isPasswordSubmitting) return;
    setPasswordMember(null);
    setPasswordValue("");
    setPasswordConfirmValue("");
  };

  const onPasswordUpdate = async () => {
    if (!sitePermission?.site || !passwordMember) return;

    const password = passwordValue.trim();
    if (password.length < 4) {
      toast.error("비밀번호는 최소 4자리 이상이어야 합니다.");
      return;
    }
    if (password !== passwordConfirmValue.trim()) {
      toast.error("비밀번호가 일치하지 않습니다.");
      return;
    }

    setIsPasswordSubmitting(true);
    try {
      await membershipService.updateMemberPassword(sitePermission.site, passwordMember.user_id, {
        password,
      });
      toast.success("비밀번호를 변경했습니다.");
      setPasswordMember(null);
      setPasswordValue("");
      setPasswordConfirmValue("");
    } catch (error) {
      console.log(error);
      const apiData = error?.response?.data;
      toast.error(apiData?.detail || "비밀번호 변경에 실패했습니다.");
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="멤버 관리" pageTitle="멤버" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="g-3 align-items-center">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">등록 멤버 목록</h5>
                    </div>
                    <div className="col-sm-auto d-flex gap-2">
                      <Button
                        color="soft-success"
                        onClick={() => setIsRegisterModalOpen(true)}
                      >
                        회원등록
                      </Button>
                      <Button color="soft-primary" onClick={() => navigate("/member-invite")}>
                        멤버 초대
                      </Button>
                    </div>
                  </Row>
                  <p className="text-muted mt-3 mb-0">
                    현재 관리자로 등록된 멤버를 조회하고, 권한 수정/삭제를 수행합니다.
                  </p>
                </CardHeader>

                <div className="card-body pt-0">
                  {isLoading ? (
                    <p className="py-3">불러오는 중...</p>
                  ) : members.length === 0 ? (
                    <p className="py-3 mb-0">등록된 멤버가 없습니다.</p>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <table className="table table-hover table-bordered">
                        <thead className="bg-slate-50">
                          <tr>
                            <th>이름</th>
                            <th>전화번호</th>
                            <th>이메일</th>
                            <th>권한</th>
                            <th>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => (
                            <tr key={member.user_id}>
                              <td>{member.username || "-"}</td>
                              <td>{member.phone_num}</td>
                              <td>{member.email || "-"}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span>
                                    {member.permission_label ||
                                      permissionLabelMap[member.permission] ||
                                      "-"}
                                  </span>
                                  {canManage ? (
                                    <select
                                      className="form-select form-select-sm"
                                      value={member.permission}
                                      onChange={(event) =>
                                        onPermissionUpdate(member, Number(event.target.value))
                                      }
                                    >
                                      {ensureCurrentPermissionOption(
                                        permissionOptions,
                                        member.permission
                                      ).map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : null}
                                </div>
                              </td>
                              <td>
                                {canManage ? (
                                  <div className="d-flex flex-wrap gap-2">
                                    <Button
                                      size="sm"
                                      color="soft-secondary"
                                      onClick={() => openPasswordModal(member)}
                                    >
                                      비밀번호 변경
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="soft-danger"
                                      onClick={() => onDeleteMember(member)}
                                    >
                                      삭제
                                    </Button>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <MemberRegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onRegistered={fetchMembers}
        permissionOptions={permissionOptions}
        siteId={sitePermission?.site}
      />
      <Modal isOpen={Boolean(passwordMember)} onClose={closePasswordModal} centered size="md">
        <ModalBody>
          <h5 className="mb-3">비밀번호 변경</h5>
          <div className="rounded border border-dashed p-3 mb-3">
            <p className="mb-1">이름: {passwordMember?.username || "-"}</p>
            <p className="mb-0">전화번호: {passwordMember?.phone_num || "-"}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="member-new-password" className="form-label">
              새 비밀번호
            </label>
            <input
              id="member-new-password"
              type="password"
              className="form-control"
              value={passwordValue}
              onChange={(event) => setPasswordValue(event.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="mb-0">
            <label htmlFor="member-new-password-confirm" className="form-label">
              새 비밀번호 확인
            </label>
            <input
              id="member-new-password-confirm"
              type="password"
              className="form-control"
              value={passwordConfirmValue}
              onChange={(event) => setPasswordConfirmValue(event.target.value)}
              autoComplete="new-password"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closePasswordModal} disabled={isPasswordSubmitting}>
            취소
          </Button>
          <Button color="success" onClick={onPasswordUpdate} disabled={isPasswordSubmitting}>
            {isPasswordSubmitting ? "변경 중..." : "변경"}
          </Button>
        </ModalFooter>
      </Modal>
      <Toaster position="top-center" />
    </React.Fragment>
  );
};

export default MemberListPage;
